import * as React from 'react';
import type { TenantFeatureMode, TenantSupplierSummary } from '../../domain/models.js';
import { repository } from '../../repositories/index.js';
import { StatusBadge } from '../../components/StatusBadge.js';

const numberFormatter = new Intl.NumberFormat('ru-RU');

function SupplierDetail({ supplier, onBack, readOnly }: { supplier: TenantSupplierSummary; onBack: () => void; readOnly: boolean }) {
  return (
    <div className="page supplier-detail-page" data-testid="supplier-detail" data-read-only={readOnly ? 'true' : 'false'}>
      <header className="page-header with-actions">
        <div>
          <button type="button" className="back-link" onClick={onBack}>← Поставщики</button>
          <h1>{supplier.name}</h1>
          <p>Операционное состояние источника без отображения секретов или учётных данных.</p>
        </div>
        <div className="header-action-group">
          {readOnly ? <span className="readonly-chip">Только чтение</span> : null}
          <StatusBadge state={supplier.health} label={supplier.healthLabel} detail={`${supplier.healthDetail} Последнее успешное обновление: ${supplier.lastSuccessfulUpdate}.`} />
        </div>
      </header>

      <div className="detail-grid two-columns">
        <section className="panel">
          <h2>Идентификация и источник</h2>
          <dl className="definition-list">
            <div><dt>Поставщик</dt><dd>{supplier.name}</dd></div>
            <div><dt>Основной метод</dt><dd>{supplier.integrationMethod}</dd></div>
            <div><dt>Последний успех</dt><dd>{supplier.lastSuccessfulUpdate}</dd></div>
            <div><dt>Состояние источника</dt><dd>{supplier.healthLabel}</dd></div>
            <div><dt>Безопасное состояние ошибки</dt><dd>{supplier.safeErrorState}</dd></div>
            {supplier.fallbackReference ? <div><dt>Fallback/reference</dt><dd>{supplier.fallbackReference}</dd></div> : null}
            <div><dt>Учётные данные</dt><dd>Не отображаются · серверная зона</dd></div>
          </dl>
        </section>
        <section className="panel">
          <h2>Возможности</h2>
          <div className="chip-list">
            {supplier.capabilities.map((capability) => <span className="capability-chip" key={capability}>{capability}</span>)}
          </div>
          <p className="panel-note">Возможности источника являются операционными фактами. Корзина, резервирование и оформление заказов не реализованы.</p>
        </section>
      </div>

      <div className="detail-grid two-columns">
        <section className="panel">
          <h2>Склады</h2>
          <div className="compact-table">
            <div className="compact-row header"><span>Склад</span><span>Состояние</span><span>Последний успех</span></div>
            {supplier.warehouses.map((warehouse) => (
              <div className="compact-row" key={warehouse.id} data-warehouse-id={warehouse.id}>
                <span>{warehouse.name}</span>
                <span className={warehouse.state === 'current' ? 'text-success' : warehouse.state === 'source_error' ? 'text-danger' : 'text-warning'}>● {warehouse.stateLabel}</span>
                <span>{warehouse.lastSuccessfulUpdate}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="panel mapping-coverage-panel">
          <h2>Покрытие сопоставлений</h2>
          <strong className="coverage-number">{supplier.mappingCoveragePercent.toLocaleString('ru-RU')}%</strong>
          <div className="progress-track" aria-label={`Покрытие ${supplier.mappingCoverageLabel}`}><span style={{ width: `${supplier.mappingCoveragePercent}%` }} /></div>
          <p>{supplier.mappingCoverageLabel}. Требуют внимания: {numberFormatter.format(supplier.needsAttention)}.</p>
          {!readOnly ? <button type="button" className="button secondary">Открыть сопоставления</button> : null}
        </section>
      </div>

      <section className="panel">
        <h2>Последние импорты</h2>
        <div className="compact-table imports-table">
          <div className="compact-row header"><span>Время</span><span>Результат</span><span>Объекты</span><span>Примечание</span></div>
          {supplier.recentImports.map((record) => (
            <div className="compact-row" key={record.id}>
              <span>{record.startedAt}</span>
              <span className={record.result === 'success' ? 'text-success' : 'text-danger'}>{record.result === 'success' ? 'Успешно' : 'Ошибка'}</span>
              <span>{record.objectCount === null ? '—' : numberFormatter.format(record.objectCount)}</span>
              <span>{record.note}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

interface SuppliersPageProps {
  mode?: TenantFeatureMode;
}

export function SuppliersPage({ mode = 'tenant' }: SuppliersPageProps) {
  const readOnly = mode === 'support-readonly';
  const [suppliers, setSuppliers] = React.useState<TenantSupplierSummary[]>([]);
  const [selected, setSelected] = React.useState<TenantSupplierSummary | null>(null);

  React.useEffect(() => {
    repository.listTenantSuppliers().then(setSuppliers);
  }, []);

  if (selected) return <SupplierDetail supplier={selected} readOnly={readOnly} onBack={() => setSelected(null)} />;

  return (
    <div className="page" data-testid="suppliers-page" data-read-only={readOnly ? 'true' : 'false'}>
      <header className="page-header with-actions">
        <div>
          <p className="eyebrow">ТЕНАНТ · ИСТОЧНИКИ</p>
          <h1>Поставщики</h1>
          <p>Метод получения, свежесть, возможности, объём источника и покрытие сопоставлений.</p>
        </div>
        {readOnly ? <span className="readonly-chip">Только чтение</span> : null}
      </header>
      <section className="panel table-panel">
        <div className="data-table supplier-table" role="table">
          <div className="data-row data-header" role="row"><span>Поставщик</span><span>Метод</span><span>Последний успех</span><span>Состояние</span><span>Возможности</span><span>Товары</span><span>Склады</span><span>Сопоставления</span><span /></div>
          {suppliers.map((supplier) => (
            <div className="data-row" role="row" key={supplier.id} data-testid={`supplier-${supplier.id}`}>
              <strong>{supplier.name}</strong>
              <span>{supplier.integrationMethod}</span>
              <span>{supplier.lastSuccessfulUpdate}</span>
              <StatusBadge state={supplier.health} label={supplier.healthLabel} detail={`${supplier.healthDetail} Последний успех: ${supplier.lastSuccessfulUpdate}.`} compact phoneCompact />
              <span className="supplier-capabilities">{supplier.capabilities.join(' · ')}</span>
              <span>{numberFormatter.format(supplier.productCount)}</span>
              <span>{supplier.warehouseCount}</span>
              <span><strong>{supplier.mappingCoverageLabel}</strong><small>{numberFormatter.format(supplier.needsAttention)} требуют внимания</small></span>
              <button type="button" className="button small secondary" onClick={() => setSelected(supplier)}>Открыть</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
